import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Pill, Search, Plus, X, Check, ChevronRight, ChevronDown,
  Sun, Moon, Clock, Dumbbell, Heart, Shield, Flame,
  Zap, Loader2, Trash2, Coffee, Droplets, Leaf, Star,
  Timer, Activity, Sparkles, Fish, Bone,
} from 'lucide-react';

/* ─── Icon map ─── */
const ICON_MAP = {
  dumbbell: Dumbbell, milk: Droplets, sun: Sun, fish: Fish,
  moon: Moon, shield: Shield, zap: Zap, citrus: Star,
  leaf: Leaf, coffee: Coffee, flame: Flame, bone: Bone,
  sparkles: Sparkles, heart: Heart, pill: Pill, droplet: Droplets,
  activity: Activity, timer: Timer,
};

const TIMING_META = {
  matin: { label: 'Matin', icon: Sun, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  midi: { label: 'Midi', icon: Clock, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  pre_workout: { label: 'Pre-entrainement', icon: Zap, color: 'text-orange-400', bg: 'bg-orange-500/10' },
  post_workout: { label: 'Post-entrainement', icon: Dumbbell, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  soir: { label: 'Soir', icon: Moon, color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
};

const CAT_COLORS = {
  performance: 'from-orange-500/15 to-red-500/10 border-orange-500/20',
  sante: 'from-emerald-500/15 to-teal-500/10 border-emerald-500/20',
};

/* ─── Tab Button ─── */
function TabBtn({ active, children, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`px-3.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all whitespace-nowrap ${active ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'bg-card/50 text-muted-foreground hover:text-foreground'}`}
    >{children}</button>
  );
}

/* ─── Supplement Detail Card (Encyclopedia) ─── */
function EncyclopediaCard({ supp, onAdd, inStack, fr }) {
  const [open, setOpen] = useState(false);
  const Icon = ICON_MAP[supp.icon] || Pill;
  const catColor = CAT_COLORS[supp.category] || CAT_COLORS.sante;
  const timing = TIMING_META[supp.timing] || TIMING_META.matin;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid={`encyclopedia-${supp.slug}`}>
      <Card className={`border shadow-lg overflow-hidden cursor-pointer transition-all hover:shadow-xl ${catColor}`} onClick={() => setOpen(!open)}>
        <CardContent className="p-0">
          <div className="p-3.5 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${supp.category === 'performance' ? 'bg-orange-500/20' : 'bg-emerald-500/20'}`}>
              <Icon className={`w-5 h-5 ${supp.category === 'performance' ? 'text-orange-400' : 'text-emerald-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate">{fr ? supp.name_fr : supp.name}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{supp.category === 'performance' ? 'Performance' : (fr ? 'Santé' : 'Health')} &bull; {supp.dosage}</p>
            </div>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>

          <AnimatePresence>
            {open && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                <div className="px-3.5 pb-3.5 space-y-3 border-t border-white/[0.04] pt-3">
                  {/* What is it */}
                  <div>
                    <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest mb-1">{fr ? "Qu'est-ce que c'est?" : 'What is it?'}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{supp.what_is_it}</p>
                  </div>
                  {/* Benefits */}
                  <div>
                    <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-widest mb-1">{fr ? 'Benefices' : 'Benefits'}</p>
                    <div className="space-y-1">
                      {supp.benefits?.map((b, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <Check className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
                          <span>{b}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Dosage & Timing */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 rounded-lg bg-white/[0.03]">
                      <p className="text-[8px] font-bold text-muted-foreground uppercase">Dosage</p>
                      <p className="text-[11px] font-semibold">{supp.dosage}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-white/[0.03]">
                      <p className="text-[8px] font-bold text-muted-foreground uppercase">{fr ? 'Moment' : 'Timing'}</p>
                      <div className="flex items-center gap-1">
                        <timing.icon className={`w-3 h-3 ${timing.color}`} />
                        <p className="text-[11px] font-semibold">{supp.timing_label}</p>
                      </div>
                    </div>
                  </div>
                  {/* Interactions */}
                  <div className="p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15">
                    <p className="text-[8px] font-bold text-amber-400 uppercase mb-1">{fr ? 'Interactions / Securite' : 'Interactions / Safety'}</p>
                    <p className="text-[10px] text-muted-foreground leading-relaxed">{supp.interactions}</p>
                  </div>
                  {/* Add to stack button */}
                  <Button size="sm" className={`w-full text-xs ${inStack ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30' : 'bg-indigo-600 hover:bg-indigo-500 text-white'}`}
                    disabled={inStack} onClick={(e) => { e.stopPropagation(); onAdd(supp); }} data-testid={`add-to-stack-${supp.slug}`}>
                    {inStack ? <><Check className="w-3.5 h-3.5 mr-1" /> {fr ? 'Dans ta stack' : 'In your stack'}</> : <><Plus className="w-3.5 h-3.5 mr-1" /> {fr ? 'Ajouter a ma stack' : 'Add to my stack'}</>}
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
}

/* ════════════════════════════════════════════════════════ */
export default function Supplements() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [tab, setTab] = useState('checklist');
  const [encyclopedia, setEncyclopedia] = useState([]);
  const [stack, setStack] = useState([]);
  const [todayIds, setTodayIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState('');

  // Custom supplement form
  const [showCustom, setShowCustom] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customDosage, setCustomDosage] = useState('');
  const [customTiming, setCustomTiming] = useState('matin');

  const fetchData = useCallback(async () => {
    try {
      const [encRes, protRes, todayRes] = await Promise.all([
        api.get('/supplements/encyclopedia'),
        api.get('/supplements/protocol'),
        api.get('/supplements/today'),
      ]);
      setEncyclopedia(encRes.data.supplements || []);
      setStack(protRes.data.protocol?.supplements || []);
      setTodayIds(todayRes.data.taken_ids || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Assistant IA deep-link: /supplements?action=log&name=creatine&dose=5 or ?action=add
  const [aiApplied, setAiApplied] = useState(false);
  useEffect(() => {
    if (aiApplied) return;
    const sp = new URLSearchParams(window.location.search);
    const action = sp.get('action');
    const name = sp.get('name');
    const dose = sp.get('dose');
    if (!action && !name && !dose) return;

    if ((action === 'log' || action === 'add') && name) {
      setCustomName(name.slice(0, 60));
      if (dose) setCustomDosage(String(dose).slice(0, 30));
      setShowCustom(true);
      setTab('stack');
    } else if (action === 'log' && !name) {
      setTab('checklist');
    }
    setAiApplied(true);
    // Clean URL
    const next = new URLSearchParams(sp);
    ['action', 'name', 'dose'].forEach(k => next.delete(k));
    const newSearch = next.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
  }, [aiApplied]);

  const stackSlugs = new Set(stack.map(s => s.slug).filter(Boolean));

  /* ─── Add from encyclopedia ─── */
  const handleAddFromEnc = async (supp) => {
    try {
      const res = await api.post('/supplements/stack/add-from-encyclopedia', { slug: supp.slug });
      if (res.data.success) {
        setStack(prev => [...prev, res.data.supplement]);
        toast.success(fr ? `${supp.name_fr} ajoute a ta stack!` : `${supp.name} added to your stack!`);
      } else {
        toast.info(res.data.message);
      }
    } catch { toast.error('Erreur'); }
  };

  /* ─── Add custom supplement ─── */
  const handleAddCustom = async () => {
    if (!customName.trim()) return;
    const newSup = {
      id: `sup_custom_${Date.now()}`,
      slug: '',
      name: customName.trim(),
      dosage: customDosage.trim(),
      unit: '',
      timing: customTiming,
      is_custom: true,
      notes: '',
    };
    const updatedStack = [...stack, newSup];
    try {
      await api.post('/supplements/protocol', { supplements: updatedStack });
      setStack(updatedStack);
      setShowCustom(false);
      setCustomName('');
      setCustomDosage('');
      toast.success(fr ? 'Supplement personnalise ajoute!' : 'Custom supplement added!');
    } catch { toast.error('Erreur'); }
  };

  /* ─── Remove from stack ─── */
  const handleRemove = async (supId) => {
    try {
      await api.delete(`/supplements/stack/${supId}`);
      setStack(prev => prev.filter(s => s.id !== supId));
      toast.success(fr ? 'Retire de ta stack' : 'Removed from stack');
    } catch { toast.error('Erreur'); }
  };

  /* ─── Toggle intake log ─── */
  const handleToggle = async (supId) => {
    try {
      const res = await api.post('/supplements/log', { supplement_id: supId });
      if (res.data.toggled_off) {
        setTodayIds(prev => prev.filter(id => id !== supId));
      } else {
        setTodayIds(prev => [...prev, supId]);
      }
    } catch { toast.error('Erreur'); }
  };

  /* ─── Filter encyclopedia ─── */
  const filteredEnc = searchQ
    ? encyclopedia.filter(s => s.name?.toLowerCase().includes(searchQ.toLowerCase()) || s.name_fr?.toLowerCase().includes(searchQ.toLowerCase()) || s.category?.toLowerCase().includes(searchQ.toLowerCase()))
    : encyclopedia;

  /* ─── Group stack by timing ─── */
  const groupByTiming = (items) => {
    const groups = {};
    const order = ['matin', 'midi', 'pre_workout', 'post_workout', 'soir'];
    for (const s of items) {
      const t = s.timing || 'matin';
      if (!groups[t]) groups[t] = [];
      groups[t].push(s);
    }
    return order.filter(t => groups[t]).map(t => ({ timing: t, items: groups[t] }));
  };

  const takenCount = stack.filter(s => todayIds.includes(s.id)).length;
  const totalCount = stack.length;

  if (loading) return <Layout><div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;

  return (
    <Layout>
      <div className="max-w-lg mx-auto pb-24 p-4 space-y-5" data-testid="supplements-page">

        {/* ═══ Header ═══ */}
        <div>
          <h1 className="text-xl font-heading uppercase tracking-wider flex items-center gap-2.5" data-testid="supplements-title">
            <Pill className="w-6 h-6 text-indigo-400" />
            {fr ? 'Protocole Supplements' : 'Supplement Protocol'}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {fr ? "Ton arsenal de supplementation quotidien." : 'Your daily supplementation arsenal.'}
          </p>
        </div>

        {/* ═══ Progress Ring (if stack has items) ═══ */}
        {totalCount > 0 && (
          <Card className="border-0 bg-gradient-to-br from-indigo-500/8 via-card to-purple-500/5 shadow-xl" data-testid="supplements-progress">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="relative w-16 h-16">
                <svg viewBox="0 0 40 40" className="w-full h-full transform -rotate-90">
                  <circle cx="20" cy="20" r="16" fill="none" stroke="currentColor" strokeWidth="3" className="text-white/[0.04]" />
                  <motion.circle cx="20" cy="20" r="16" fill="none" stroke="url(#sup-grad)" strokeWidth="3" strokeLinecap="round"
                    strokeDasharray={100.53} initial={{ strokeDashoffset: 100.53 }}
                    animate={{ strokeDashoffset: totalCount > 0 ? 100.53 - (takenCount / totalCount) * 100.53 : 100.53 }}
                    transition={{ duration: 0.8 }}
                  />
                  <defs><linearGradient id="sup-grad"><stop offset="0%" stopColor="#818cf8" /><stop offset="100%" stopColor="#c084fc" /></linearGradient></defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-black">{takenCount}/{totalCount}</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-bold">{fr ? 'Prise du jour' : 'Today\'s intake'}</p>
                <p className="text-[10px] text-muted-foreground">
                  {takenCount === totalCount && totalCount > 0 ? (fr ? 'Tout est pris! Bien joue!' : 'All taken! Well done!') : (fr ? `${totalCount - takenCount} supplements restants` : `${totalCount - takenCount} supplements remaining`)}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══ Tabs ═══ */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="supplements-tabs">
          <TabBtn active={tab === 'checklist'} onClick={() => setTab('checklist')} testId="tab-checklist">{fr ? 'Checklist' : 'Checklist'}</TabBtn>
          <TabBtn active={tab === 'stack'} onClick={() => setTab('stack')} testId="tab-stack">{fr ? 'Ma Stack' : 'My Stack'}</TabBtn>
          <TabBtn active={tab === 'encyclopedia'} onClick={() => setTab('encyclopedia')} testId="tab-encyclopedia">{fr ? 'Encyclopedie' : 'Encyclopedia'}</TabBtn>
        </div>

        {/* ═══ TAB: Checklist ═══ */}
        {tab === 'checklist' && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="checklist-tab">
            {stack.length === 0 ? (
              <div className="text-center py-12 space-y-3">
                <Pill className="w-12 h-12 text-muted-foreground/20 mx-auto" />
                <p className="text-sm text-muted-foreground">{fr ? 'Aucun supplement dans ta stack.' : 'No supplements in your stack.'}</p>
                <Button variant="outline" size="sm" onClick={() => setTab('encyclopedia')} className="text-xs">
                  <Search className="w-3.5 h-3.5 mr-1" /> {fr ? 'Explorer l\'encyclopedie' : 'Explore encyclopedia'}
                </Button>
              </div>
            ) : (
              groupByTiming(stack).map(({ timing, items }) => {
                const meta = TIMING_META[timing] || TIMING_META.matin;
                const TimingIcon = meta.icon;
                return (
                  <div key={timing} className="space-y-2" data-testid={`checklist-group-${timing}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-lg ${meta.bg} flex items-center justify-center`}>
                        <TimingIcon className={`w-3.5 h-3.5 ${meta.color}`} />
                      </div>
                      <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">{meta.label}</p>
                    </div>
                    {items.map(s => {
                      const taken = todayIds.includes(s.id);
                      return (
                        <motion.div key={s.id} whileTap={{ scale: 0.98 }}
                          className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${taken ? 'bg-emerald-500/8 border-emerald-500/20' : 'bg-card/40 border-border/30 hover:bg-card/60'}`}
                          onClick={() => handleToggle(s.id)} data-testid={`checklist-item-${s.id}`}>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${taken ? 'bg-emerald-500 border-emerald-500' : 'border-muted-foreground/30'}`}>
                            {taken && <Check className="w-3.5 h-3.5 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold ${taken ? 'text-emerald-400 line-through' : ''}`}>{s.name}</p>
                            {s.dosage && <p className="text-[9px] text-muted-foreground">{s.dosage} {s.unit}</p>}
                          </div>
                          {s.is_custom && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-400 font-bold uppercase">Custom</span>}
                        </motion.div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </motion.div>
        )}

        {/* ═══ TAB: Ma Stack ═══ */}
        {tab === 'stack' && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="stack-tab">
            <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">
              {fr ? `${stack.length} supplements dans ta stack` : `${stack.length} supplements in your stack`}
            </p>

            {groupByTiming(stack).map(({ timing, items }) => {
              const meta = TIMING_META[timing] || TIMING_META.matin;
              const TimingIcon = meta.icon;
              return (
                <div key={timing} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <TimingIcon className={`w-3.5 h-3.5 ${meta.color}`} />
                    <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">{meta.label}</p>
                  </div>
                  {items.map(s => (
                    <Card key={s.id} className="border-0 bg-card/50 shadow-lg" data-testid={`stack-item-${s.id}`}>
                      <CardContent className="p-3 flex items-center gap-3">
                        <Pill className="w-4 h-4 text-indigo-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate">{s.name}</p>
                          <p className="text-[9px] text-muted-foreground">{s.dosage} {s.unit} {s.is_custom ? '(personnalise)' : ''}</p>
                        </div>
                        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-400 h-8 w-8 p-0"
                          onClick={() => handleRemove(s.id)} data-testid={`remove-stack-${s.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })}

            {/* Add custom supplement */}
            <AnimatePresence>
              {showCustom && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
                  <Card className="border border-purple-500/20 bg-card/60 shadow-lg">
                    <CardContent className="p-3.5 space-y-3">
                      <p className="text-xs font-bold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-purple-400" /> {fr ? 'Supplement personnalise' : 'Custom supplement'}</p>
                      <Input placeholder={fr ? 'Nom (ex: Magnésium Threonate)' : 'Name (e.g. Magnésium Threonate)'} value={customName} onChange={e => setCustomName(e.target.value)} className="text-sm" data-testid="custom-name-input" />
                      <Input placeholder={fr ? 'Dosage (ex: 200mg)' : 'Dosage (e.g. 200mg)'} value={customDosage} onChange={e => setCustomDosage(e.target.value)} className="text-sm" data-testid="custom-dosage-input" />
                      <div className="flex gap-1.5 flex-wrap">
                        {Object.entries(TIMING_META).map(([key, meta]) => (
                          <button key={key} onClick={() => setCustomTiming(key)} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase transition-all ${customTiming === key ? 'bg-purple-600 text-white' : 'bg-card/50 text-muted-foreground'}`}>
                            <meta.icon className="w-3 h-3" /> {meta.label}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1 bg-purple-600 hover:bg-purple-500 text-white text-xs" onClick={handleAddCustom} disabled={!customName.trim()} data-testid="save-custom-btn">
                          <Check className="w-3.5 h-3.5 mr-1" /> {fr ? 'Ajouter' : 'Add'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setShowCustom(false)}><X className="w-3.5 h-3.5" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 text-xs" onClick={() => setTab('encyclopedia')} data-testid="browse-encyclopedia-btn">
                <Search className="w-3.5 h-3.5 mr-1" /> {fr ? 'Encyclopedie' : 'Encyclopedia'}
              </Button>
              <Button variant="outline" className="flex-1 text-xs" onClick={() => setShowCustom(!showCustom)} data-testid="add-custom-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> {fr ? 'Personnalise' : 'Custom'}
              </Button>
            </div>
          </motion.div>
        )}

        {/* ═══ TAB: Encyclopedia ═══ */}
        {tab === 'encyclopedia' && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="encyclopedia-tab">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder={fr ? 'Rechercher un supplement...' : 'Search supplement...'} value={searchQ} onChange={e => setSearchQ(e.target.value)}
                className="pl-9 text-sm" data-testid="encyclopedia-search" />
              {searchQ && (
                <button onClick={() => setSearchQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Category filters */}
            <div className="flex gap-2">
              <button onClick={() => setSearchQ('')} className={`px-3 py-1 rounded-full text-[9px] font-bold uppercase ${!searchQ ? 'bg-indigo-600 text-white' : 'bg-card/50 text-muted-foreground'}`}>{fr ? 'Tout' : 'All'}</button>
              <button onClick={() => setSearchQ('performance')} className={`px-3 py-1 rounded-full text-[9px] font-bold uppercase ${searchQ === 'performance' ? 'bg-orange-600 text-white' : 'bg-card/50 text-muted-foreground'}`}>Performance</button>
              <button onClick={() => setSearchQ('santé')} className={`px-3 py-1 rounded-full text-[9px] font-bold uppercase ${searchQ === 'santé' ? 'bg-emerald-600 text-white' : 'bg-card/50 text-muted-foreground'}`}>{fr ? 'Santé' : 'Health'}</button>
            </div>

            <p className="text-[10px] text-muted-foreground">{filteredEnc.length} {fr ? 'supplements' : 'supplements'}</p>

            <div className="space-y-2.5">
              {filteredEnc.map(s => (
                <EncyclopediaCard key={s.slug} supp={s} fr={fr} inStack={stackSlugs.has(s.slug)} onAdd={handleAddFromEnc} />
              ))}
            </div>
          </motion.div>
        )}

      </div>
    </Layout>
  );
}
